<script setup lang="ts">
import type { ArchProps } from "./Arch.props"
import { archCarouselOptions } from "./Arch.carouselOptions"
import { sm } from "~/utils/breakpoints"
import { ComponentArchCarouselItemProps } from "../cms/ComponentArchCarouselItem.props"
import { isString } from "lodash"

const props = defineProps<ArchProps>()

// Carousel loading state
const isCarouselLoaded = ref(false)
const carouselContainer = ref<HTMLElement>()

const carouselOptions = computed(() => archCarouselOptions)
const smBackgroundImage = computed(
  () => props.backgroundImage?.mobileImage?.image?.url
)
const lgBackgroundImage = computed(
  () => props.backgroundImage?.desktopImage?.image?.url
)
const backgroundImage = computed(() =>
  sm.value ? smBackgroundImage.value : lgBackgroundImage.value
)

const backgroundStyle = computed(() => ({
  backgroundImage: `url(${backgroundImage.value})`,
  backgroundSize: "cover",
  backgroundPosition: "center"
}))

// Function to filter items based on the presence of a valid link
const filterCarouselItems = computed(() => {
  return props.carouselItems.filter((item: ComponentArchCarouselItemProps) => {
    const href = isString(item?.link) ? item?.link : item?.link?.path
    return href && href !== "#"
  })
})

// Carousel loading management
onMounted(() => {
  // Wait for next tick to ensure DOM is ready
  nextTick(() => {
    // Add timeout to allow Swiper to fully initialize
    setTimeout(() => {
      isCarouselLoaded.value = true
    }, 50)
  })
})

// Reset loading state when items change
watch(
  () => props.carouselItems,
  () => {
    if (props.carouselItems.length > 0) {
      isCarouselLoaded.value = false
      nextTick(() => {
        setTimeout(() => {
          isCarouselLoaded.value = true
        }, 50)
      })
    }
  },
  { deep: true }
)

// Number of skeletons to show based on carousel breakpoints
// Using breakpoints directly from carousel options to be safe
const skeletonCount = computed(() => {
  // For now using fixed number appropriate for most cases
  // This avoids SSR issues and window not available
  if (sm.value) return 4 // Mobile: 4 skeletons to cover up to 3.2 slides
  return 6 // Desktop: 6 skeletons to cover up to 5.5 slides
})
</script>

<template>
  <section
    ref="carouselContainer"
    class="
      padded
      carousel-container-overflowed
      arch-carousel-wrapper
      py-6
      md:py-7
    "
    :style="backgroundStyle"
  >
    <div class="mb-4 flex w-full items-center justify-between md:mb-6">
      <section class="flex flex-col gap-1">
        <p v-if="title" class="elephant-bold md:mammoth-bold">
          {{ title }}
        </p>
      </section>
    </div>

    <!-- Relative container for carousel and skeleton -->
    <div
      class="relative"
      :class="{
        'min-h-carousel-skeleton':
          !isCarouselLoaded && filterCarouselItems.length
      }"
    >
      <!-- Carousel that defines the height -->
      <div
        v-if="filterCarouselItems.length"
        :class="{ 'pointer-events-none opacity-0': !isCarouselLoaded }"
      >
        <AppCarousel
          class="carousel-overflowed select-none"
          :swiper-props="carouselOptions"
          :items="filterCarouselItems"
          :theme="carouselOptions?.theme"
          :key="sm ? 1 : 0"
        >
          <template #default="{ item }">
            <CardArchedDefault v-bind="item" class="swiper-tile" />
          </template>
        </AppCarousel>
      </div>

      <!-- Overlaid Skeleton Loaders -->
      <div
        v-if="!isCarouselLoaded && filterCarouselItems.length"
        class="skeleton-carousel-grid absolute inset-0"
      >
        <div
          v-for="index in skeletonCount"
          :key="`skeleton-${index}`"
          class="skeleton-slide"
        >
          <!-- Skeleton that replicates CardArchedDefault structure -->
          <div class="block h-full">
            <div class="inline-block h-full w-full">
              <!-- Arched rounded image -->
              <div
                class="
                  relative
                  mx-auto
                  aspect-square
                  max-h-[228px] max-w-[228px]
                  overflow-hidden
                  rounded-[130px_130px_10px_10px]
                "
              >
                <SkeletonAnimatedPlaceholder
                  height="100%"
                  width="100%"
                  border-radius="130px 130px 10px 10px"
                  box-shadow="01"
                />
              </div>

              <!-- Title below the image -->
              <div class="mt-1 flex justify-center md:mt-4">
                <SkeletonAnimatedPlaceholder
                  height="20px"
                  :width="sm ? '80px' : '120px'"
                  border-radius="4px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
:deep(.swiper-slide:first-child > div) {
  margin-left: 0;
}

:deep(.swiper-slide:last-child > div) {
  margin-right: 0;
}

:deep(.swiper.swiper-color-navigation-dark-rails-promo) {
  padding-bottom: 35px;
  @screen md {
    padding-bottom: 100px;
  }
}

.swiper-tile {
  margin: 0;
}

/* Skeleton grid that replicates the carousel */
.skeleton-carousel-grid {
  display: flex;
  gap: 16px;
  overflow: visible;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: transparent;

  @media (min-width: 414px) {
    gap: 20px;
  }
}

.skeleton-slide {
  flex: 0 0 calc((100% - 16px) / 2.2);

  @media (min-width: 414px) {
    flex: 0 0 calc((100% - 40px) / 3.2);
  }

  @media (min-width: 640px) {
    flex: 0 0 calc((100% - 64px) / 4.2);
  }

  @media (min-width: 1024px) {
    flex: 0 0 calc((100% - 64px) / 4.2);
  }

  @media (min-width: 1200px) {
    flex: 0 0 calc((100% - 64px) / 5);
  }

  @media (min-width: 1601px) {
    flex: 0 0 calc((100% - 80px) / 5.5);
  }
}

/* Minimum height for container when showing skeleton */
.min-h-carousel-skeleton {
  /* Base: .padded has px-4 (32px total)
     Effective container = 100vw - 32px
     Slide width = (container - gaps) / slidesPerView */
  min-height: calc(((100vw - 32px - 16px) / 2.2) + 40px);

  @media (min-width: 414px) {
    /* 414px+: still px-4, gap increases to 20px */
    min-height: calc(((100vw - 32px - 40px) / 3.2) + 44px);
  }

  @media (min-width: 768px) {
    /* md: .padded becomes px-4 */
    min-height: calc(((100vw - 32px - 64px) / 4.2) + 48px);
  }

  @media (min-width: 1024px) {
    /* lg+: .padded becomes px-[71px] (142px total) */
    min-height: calc(((100vw - 142px - 64px) / 4.2) + 48px);
  }

  @media (min-width: 1200px) {
    /* 1200px+: px-[71px] + 5 slides */
    min-height: calc(((100vw - 142px - 64px) / 5) + 48px);
  }

  @media (min-width: 1601px) {
    /* 1601px+: px-[228px] + 5.5 slides */
    min-height: calc(((100vw - 456px - 80px) / 5.5) + 48px);
  }
}

/* Smooth transition for carousel */
.carousel-overflowed {
  transition: opacity 0.1s ease-in-out;
}
</style>
